import type TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';

import type { DecisionMaker } from '../types';

const DECISION_MAKERS_CACHE_SECONDS = 2 * 60;

const DECISION_MAKERS_COLLECTION = 'decision_makers';
export const DECISION_MAKERS_PRESET = 'decision_makers_preset';

// Field names as they exist in the Typesense decision_makers collection
type DecisionMakerHit = {
	id: string;
	name: string;
	summary: string;
	photo_url?: string;
	is_person: boolean;
	is_confirmed: boolean;
};

type Params = {
	searchQuery: string;
	countryCode: string;
	imageHost: string;
};

function resolvePhotoUrl(photoUrl: string | null, imageHost: string): string | null {
	if (!photoUrl) return null;
	return `${imageHost}${photoUrl.replace('-fullsize.', '-48x48-noPad.')}`;
}

export async function searchDecisionMakersTypesense(
	{ searchQuery, countryCode, imageHost }: Params,
	typesenseClient: TypesenseInstantsearchAdapter['typesenseClient'],
): Promise<readonly DecisionMaker[]> {
	if (searchQuery.trim() === '') return [];

	const response = await typesenseClient
		.collections<DecisionMakerHit>(DECISION_MAKERS_COLLECTION)
		.documents()
		.search(
			{
				q: searchQuery,
				preset: DECISION_MAKERS_PRESET,
				...(countryCode && {
					sort_by: `_eval(country_code:=${countryCode}):desc,petition_count:desc,_text_match:desc`,
				}),
			},
			{ cacheSearchResultsForSeconds: DECISION_MAKERS_CACHE_SECONDS },
		);

	const hits = response.hits ?? [];

	return [
		...hits.map(({ document }) => {
			const doc = document as DecisionMakerHit;
			const resolvedPhotoUrl = resolvePhotoUrl(doc.photo_url ?? null, imageHost);
			return {
				id: doc.id,
				displayName: doc.name,
				summary: doc.summary || null,
				isPerson: doc.is_person,
				isConfirmed: doc.is_confirmed,
				photo: resolvedPhotoUrl ? { id: doc.id, userSmall: { url: resolvedPhotoUrl } } : null,
			};
		}),
		// Adding this unconfirmed dummy DM to trigger the "Add DM" functionality
		{ id: '0', displayName: searchQuery.trim(), isPerson: null, isConfirmed: false, photo: null, summary: '' },
	];
}
